import React, { useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X, Plus, Minus } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getDefaultQuantity, getSmallLoadSurcharge } from '../utils/loadSplit';
import { CartHelper } from '../components/CartHelper';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-3149" data-mp="3lq4aw4h9k993">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-3150" data-mp="1owaoirj110as">
        <span className="truncate" data-id="element-3151" data-mp="34hbequhxbwjw">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-3152" data-mp="39c8ne0bak3w9" />
      </button>

      <AnimatePresence data-id="element-3153" data-mp="5yeamwgm3rco">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full top-full mt-2 bg-white dark:bg-stone-900 border border-[#d4ddcf] dark:border-white/10 rounded-none shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-3154" data-mp="5xcp1y1yzmgp">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#e3f7e8] dark:bg-[#0f3e1e] text-[#1a7a3a] dark:text-[#a8e8b8] font-semibold' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-3155" data-mp="2gnl05z6zf4g2">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-3156" data-mp="12qccwoovwabn" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-3157" data-mp="5yxq5efi7jtv">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-3158" data-mp="60sfd4xt5y3r" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3159" data-mp="ezm7oj43w2bi">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-3160" data-mp="jqm29hpr68au">
          <ChevronLeft className="w-5 h-5" data-id="element-3161" data-mp="tn7988lc1thb" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-3162" data-mp="xqddxbssrclo">
          <ChevronRight className="w-5 h-5" data-id="element-3163" data-mp="2zia0rhkf08vr" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-3164" data-mp="34gl5xezrlv7n">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-3165" data-mp="2wwsi4f0thq9b" data-mpi={i} />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart,
    openCart
  } = useCart();
  // Lazy-init the default quantity per product. Soil/Mulch/Org. products
  // (topsoil, bark, compost, play chips, fill dirt) default to a 15-yd
  // light load — that's the typical homeowner spring-project size and
  // the size of one full light-material truckload. The 50/50 Mix is
  // the explicit exception (defaults to 12 — see getDefaultQuantity in
  // utils/loadSplit.ts). Everything else (rock, drain, quarry, decor)
  // defaults to a 12-yd heavy load.
  const [quantity, setQuantity] = useState<string>(() => {
    for (const cat of categories) {
      const found = cat.products.find(p => p.slug === slug);
      if (found) return getDefaultQuantity(found.name);
    }
    return '12';
  });
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  // Click-to-expand explainer for the Small-load rate label that now
  // sits ABOVE the big price number when the order is under the
  // small-load threshold. Replaces the verbose "surcharge applies"
  // subtext with a one-tap pulldown of plain-language pricing context.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Auto-dismiss the "Added to Truck!" confirmation card after 10s and
  // revert to the default Add-to-Truck state. Customer can still tap
  // Continue Shopping / View Cart at any point during the window.
  useEffect(() => {
    if (!addedFlash) return;
    const t = setTimeout(() => setAddedFlash(false), 10000);
    return () => clearTimeout(t);
  }, [addedFlash]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // True when + has been pressed past 4 loads. Replaces the auto-fire
  // QuoteModal — the customer sees an amber "5+ loads" link below the
  // stepper and explicitly taps it to open the form.
  const [showQuoteWarning, setShowQuoteWarning] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync by writing to qtyDraft alongside quantity via the useEffect below.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Sync the input's displayed text whenever quantity changes externally
  // (e.g. via +/− pads, or estimatorQty handoff). Skip the sync if the
  // user is actively typing — we don't want to clobber their keystrokes.
  useEffect(() => {
    if (document.activeElement !== qtyInputRef.current) {
      setQtyDraft(String(Number(quantity) || 12));
    }
  }, [quantity]);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  const handleUseQuantity = (yards: number) => {
    const roundedYards = Math.ceil(yards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: pre-select the dropdown value at the actual yards the
      // customer asked for. Tiny 1–2 yd loads are accepted as-is and priced
      // with the micro-load formula by the cart's billing math.
      const snapped = Math.max(1, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      // Confirmation persists until the customer chooses Continue
      // Shopping or View Cart — gives them time to decide their next
      // step instead of the old 2s auto-dismiss.
      setAddedFlash(true);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    // Accept any positive yardage — manual 1–2 yd entries flow through and
    // get priced with the micro-load formula (flat $250 delivery).
    if (isNaN(n) || n < 1) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const snapped = Math.max(1, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-3166" data-mp="1d4dkix7gxeyt">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-3167" data-mp="3opmfwo16s5jd">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-3168" data-mp="3tno0xei6aexh">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '5+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    // Confirmation persists until the customer chooses Continue
    // Shopping or View Cart.
    setAddedFlash(true);
  };
  // Multi-load custom-quote redirect REMOVED. The amber 5+ warning
  // inside the readout window is now the SOLE entry point to the
  // QuoteModal — no auto-fire when numLoads flips to '5+'. The user
  // explicitly has to tap the warning to open the contact form,
  // matching the Hero estimator's behavior.
  // Reset the quantity dropdown to the per-product default whenever the
  // user navigates to a different product. React Router reuses the same
  // <Product /> component instance when only the :slug param changes, so
  // the useState lazy initializer above only runs on FIRST mount. Without
  // this effect, picking Medium Bark after viewing 1.25" Minus would
  // leave the dropdown stuck at 12 yd instead of snapping to the 15 yd
  // light-load default.
  useEffect(() => {
    if (product?.name) {
      setQuantity(getDefaultQuantity(product.name));
      setNumLoads('');
    }
    // Intentionally only re-runs on slug change — manual dropdown
    // adjustments by the customer within the same product are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#ffffff] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-3169" data-mp="m54ayqn7gyq8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3170" data-mp="7b83etyvka42">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-3171" data-mp="14x5liq9cedfh">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-3172" data-mp="zyc2yjf77w8g">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3173" data-mp="v5sozlclmz34" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-3174" data-mp="q6pxl3pjkrdm">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-3175" data-mp="1noycyg8rjql3">
                <ChevronLeft className="w-4 h-4" data-id="element-3176" data-mp="1v9io63r5mmym" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-3177" data-mp="205qx2zerdjxc">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-3178" data-mp="253sgxmk0dn9v">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-3179" data-mp="2a3x2hqzrx86y">
                <ChevronLeft className="w-4 h-4" data-id="element-3180" data-mp="2e58s3windtf5" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-3181" data-mp="2iybdhhd9twk9">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-3182" data-mp="2xsga04b01gxz">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-3183" data-mp="3e6xs86t6y0jq">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-3184" data-mp="3e64we0vrsvgd" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-3185" data-mp="3s6fn93x2ya1j">
                <ChevronRight className="w-4 h-4" data-id="element-3186" data-mp="3n85qwpzmd3ec" />
              </span>}
          </div>
        </div>

        <div className="bg-[#f6f8f9] dark:bg-[#333130] rounded-2xl border border-[#e6ece3] dark:border-[#262626] overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-3187" data-mp="2zijqy0bfak5a">
          {/* Image — 1:1 split with the content panel for a balanced
           hero feel where the product photo gets equal visual weight. */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative" data-id="element-3188" data-mp="34f1tqun708ez">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-3189" data-mp="2gq8twn3g9gf3" />
          </div>

          {/* Content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col" data-id="element-3190" data-mp="2brnswlgdispl">
            <div className="mb-2" data-id="element-3191" data-mp="r93hszq9wlfk">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-3192" data-mp="r7shxazy7xtq">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-3193" data-mp="iai2rek0fojj">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-3194" data-mp="1axm9pp2nycxy">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-3195" data-mp="1ezzhej0z0kxd">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-3196" data-mp="3lp5prtcux8q0" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-3197" data-mp="3qmnpqu8wcdlv">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-3198" data-mp="3vzb4947dcicj" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-3199" data-mp="3i6v90pkplom6">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-3200" data-mp="3d9l9gh3lvwkm" />}
              </button>
            </div>

            <div className="mb-6" data-id="element-3201" data-mp="347vl5k50tdr2">
              <AnimatePresence mode="wait" data-id="element-3202" data-mp="346tysjik14u1">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-3203" data-mp="2klttci14p1ap">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-3204" data-mp="2pjvbn0qqcwl4">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-3205" data-mp="2yka6aqpl3o1a">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-3206" data-mp="3d9zayvwvya6b">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-3207" data-mp="3maxivpag31u6">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-3208" data-mp="3hddtbt8ar2mx" data-mpi={idx}>
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-3209" data-mp="2sr4m6oqeaisq">
                        <div className="bg-[#04A05C]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-3210" data-mp="2xowe3ir1i4wx">
                          <Check className="w-4 h-4 text-[#04A05C] dark:text-[#22c95c]" data-id="element-3211" data-mp="32nhbzbitkci7" />
                        </div>
                        <div data-id="element-3212" data-mp="37on5hbkb03ss">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-3213" data-mp="3bpgsm2px9ekm">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-3214" data-mp="1dbniuyhia9c0">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-3215" data-mp="14zvn6tiifl6m">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3216" data-mp="1nr6dp9kbpvxz">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-3217" data-mp="3dxksvy9icg8">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-3218" data-mp="1vziur4wufww5">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-3219" data-mp="1ma93tsdqkk9m">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-3220" data-mp="2ew0r5zmn0kyq">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-3221" data-mp="pinn8bifsyy5">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-3222" data-mp="vy4bq04kr3yi">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-3223" data-mp="d0b80vm4mq02">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-3224" data-mp="d2e5u1titnfc">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-3225" data-mp="3gpeirw44dwlw" />
                        </div>
                        <div className="min-w-0" data-id="element-3226" data-mp="3br37dgy6fe1k">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-3227" data-mp="3bqbcy9gbfmcv">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-3228" data-mp="1klhvd4ji3gl2">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-3229" data-mp="1tgo3wba5ocra">
                        <Download className="w-4 h-4" data-id="element-3230" data-mp="11n5jvc549umb" />
                        <span className="hidden sm:inline" data-id="element-3231" data-mp="11nxeokqw3obj">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-3232" data-mp="1alzdqur2c6w6">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-3233" data-mp="2m2hbcy6ecfke">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-3234" data-mp="2w6nvrog2axb4" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-3235" data-mp="2rap33onpat4w">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-3236" data-mp="2mgkyhc1wqx6z">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-3237" data-mp="2dhhbhwzmdxn7">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@206gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-3238" data-mp="23jjo1bpl44vn">
                            info@206gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div data-id="element-3239" data-mp="l0ol7vhfrr6r">
              {isSpecialty ? <div className="space-y-4" data-id="element-3240" data-mp="kyvhop24dn2z">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-3241" data-mp="78rx02iw21fn">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-3242" data-mp="c6ti0szvkatr">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-3243" data-mp="3kfk01yosx78e">
                    <Phone className="w-5 h-5 mr-2" data-id="element-3244" data-mp="1im2sev0o86dn" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-4" data-id="element-3245" data-mp="1xqp3fejmps4a">
                  {(() => {
                const fullLoad = isLight ? 15 : 12;
                const smallLoadThreshold = 10;
                const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '5+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                const loads: number[] = [];
                if (requestedYards > 0) {
                  if (requestedYards <= fullLoad) {
                    loads.push(requestedYards);
                  } else {
                    const count = Math.ceil(requestedYards / fullLoad);
                    const last = requestedYards - (count - 1) * fullLoad;
                    for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                    loads.push(last);
                  }
                }
                const billableYards = loads.reduce((a, b) => a + b, 0);
                const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                // Build per-delivery line totals so the multi-load
                // breakdown can mirror the Services hub: each
                // delivery shows its yardage and its post-discount
                // dollar amount on its own row. preDiscountTotal is
                // the strikethrough "Was $X" anchor for the bulk
                // discount headline.
                const deliveryLoads: {
                  yards: number;
                  total: number;
                }[] = [];
                let hasSurchargedLoad = false;
                for (const ld of loads) {
                  let sur = 1;
                  if (ld >= 3 && ld < smallLoadThreshold) {
                    sur = 1 + (smallLoadThreshold - ld) * 0.1;
                  }
                  if (sur > 1) hasSurchargedLoad = true;
                  const loadTotal = Math.ceil(product.pricePerYard * sur) * ld;
                  deliveryLoads.push({
                    yards: ld,
                    total: loadTotal
                  });
                }
                const preDiscountTotal = deliveryLoads.reduce((s, l) => s + l.total, 0);
                let totalCost = preDiscountTotal;
                if (bulkPct > 0) {
                  totalCost = Math.round(totalCost * (1 - bulkPct));
                  // Apply the same bulk discount to each per-delivery
                  // total so the rows sum to the discounted grand
                  // total (with at most a rounding-cent of drift).
                  deliveryLoads.forEach((l, i) => {
                    deliveryLoads[i] = {
                      ...l,
                      total: Math.round(l.total * (1 - bulkPct))
                    };
                  });
                }
                const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                // Past 4 full loads the order rolls into custom-quote
                // territory. The bulk-discount formula no longer
                // reflects what dispatch will actually charge (hand
                // loads, mixed truck sizes, etc.), so we suppress
                // the savings copy past that threshold rather than
                // surface a stale number.
                const isCustomQuoteZone = deliveryLoads.length >= 5;
                return <div className="border-b border-stone-100 dark:border-[#444444] pb-4" data-id="element-3246" data-mp="ybvu6btu38zh">
                        <div className="flex items-start justify-between gap-4" data-id="element-3247" data-mp="1d0rxjvky3xtr">
                          {/* Left: price column. The label above the
                           big number is state-aware:
                           - Bulk discount tier reached: amber pill
                           "5% Bulk Discount" (uppercase, prominent).
                           - Under the small-load threshold: clickable
                           "Small-load rate ⓘ" — tapping toggles a
                           full-width pulldown explainer below the
                           two-column header.
                           - Otherwise: the default "Price" label. */}
                          <div className="min-w-0 flex-1" data-id="element-3248" data-mp="2m7k4ne7u8s84">
                            {/* State label — all three variants render
                             at the same H-6 height so the big "$XX
                             /yard" number stays on the same baseline
                             as the right-column total regardless of
                             which label is showing.
                             Priority order:
                             1. Bulk discount (green pill) — customer
                             paying LESS.
                             2. Small-load surcharge (amber pill) —
                             customer paying MORE. Triggers off
                             hasSurchargedLoad alone so the pill
                             appears even on small-load orders where
                             the rounded effective rate happens to
                             match the base rate.
                             3. Default "Price" label otherwise. */}
                            <div className="h-6 mb-1 flex items-center" data-id="element-3249" data-mp="1v26bzf79dsx1">
                              {!isCustomQuoteZone && bulkPct > 0 ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#04A05C] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-3250" data-mp="2sodi4tj7ntbi">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span> : !isCustomQuoteZone && hasSurchargedLoad ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#CB6E0A] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-3251" data-mp="2eovoo0uyzrvt">
                                  Small-load rate
                                </span> : <p className="text-sm text-brand-muted dark:text-[#22c95c] font-medium" data-id="element-3252" data-mp="13cuwebywdlbb">
                                  Price
                                </p>}
                            </div>
                            {isCustomQuoteZone ? <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-3253" data-mp="3ny09fkgl8ahf">
                                Custom quote
                              </p> : <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-3254" data-mp="3sr33rut6vnyo">
                                $
                                {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                                {/* Secondary "/ yard" label uses the
                                 same neutral muted-grey as the right
                                 column's "{N} yds /" and "+ delivery"
                                 so the metadata reads uniformly
                                 regardless of small-load / bulk
                                 discount state. "avg" dropped per
                                 design — the headline rate IS the
                                 customer's per-yard cost. */}
                                <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-3255" data-mp="3fnqxpjt6h1d3">
                                  / yard
                                </span>
                              </p>}
                            {/* Subtext under the big number. For a small
                             load: single line with strikethrough base
                             rate + plain "base rate at N yd+" copy
                             (the clickable label above carries the
                             surcharge messaging now). For a bulk
                             discount: single line with strikethrough
                             base rate + "Save $X on N yards". */}
                            {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 whitespace-nowrap" data-id="element-3256" data-mp="l6ncqg60phia">
                                  ${product.pricePerYard}/yd at{' '}
                                  {smallLoadThreshold} yd+
                                </p>}
                            {showsEffective && bulkPct > 0 && !isCustomQuoteZone && (
                      /* Savings copy under the price reads
                      accent green (matches the green pill
                      above) — the customer is paying LESS
                      here, so the color reinforces that
                      even when the eye skips the label. */
                      <p className="text-sm mt-1" data-id="element-3257" data-mp="qtpimids7t66">
                                  <span className="line-through text-stone-500 dark:text-stone-400" data-id="element-3258" data-mp="c4b5cz734rli">
                                    ${product.pricePerYard}/yd
                                  </span>
                                  <span className="text-[#04A05C] ml-2 font-semibold" data-id="element-3259" data-mp="32tupwcg5i47">
                                    $
                                    {product.pricePerYard * billableYards - totalCost}{' '}
                                    savings
                                  </span>
                                </p>)}
                            {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-3260" data-mp="dk74ye4hdkt7">
                                Base price at {smallLoadThreshold}+ yards per
                                delivery. Smaller loads include a surcharge.
                              </p>}
                          </div>
                          {/* Right: materials subtotal — mirrors the
                           "$87 / yard" pattern on the left by putting the
                           small qualifier ("12 yds /") IN FRONT of the
                           big number ("$1,044"). The leading-blank top
                           row keeps the big numbers baseline-aligned
                           with the Price column even though the right
                           column has no top label. */}
                          {billableYards > 0 && <div className="flex-1 min-w-0 text-right" data-id="element-3261" data-mp="1shrsqgvrk18g">
                              {/* invisible spacer matching the
                               left-column label container so both
                               big numbers land on the same baseline
                               regardless of which state label
                               (Price / Small-load rate / Bulk
                               Discount) is showing. */}
                              <div aria-hidden="true" className="h-6 mb-1 select-none" data-id="element-3262" data-mp="1dsvokuwtv8d4" />
                              {isCustomQuoteZone ? <>
                                  <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-3263" data-mp="1ir6z77uy5mwe">
                                    <span className="text-sm font-normal text-brand-muted dark:text-[#22c95c]" data-id="element-3264" data-mp="1nl1d1unvf7nk">
                                      {billableYards} yds /
                                    </span>{' '}
                                    Custom
                                  </p>
                                  <p className="text-xs text-brand-orange mt-0.5" data-id="element-3265" data-mp="377f769w1fay6">
                                    text us for pricing
                                  </p>
                                </> : <>
                                  {/* Secondary metadata ("{N} yds /"
                                   and "+ delivery") uses the same
                                   neutral muted-grey as the left
                                   column's "/ yard" so the surrounding
                                   metadata reads uniformly white →
                                   grey regardless of discount state.
                                   Only the BIG numbers stay white. */}
                                  <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-3266" data-mp="25jw8oi9tub7t">
                                    <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-3267" data-mp="2ak0pk9end7yk">
                                      {billableYards} yds /
                                    </span>{' '}
                                    ${totalCost.toLocaleString()}
                                  </p>
                                  {/* "+ delivery" mirrors the green
                                   "Price" label on the left so the
                                   two columns book-end the row in
                                   matching accent colors. */}
                                  <p className="text-xs text-brand-muted dark:text-[#22c95c] mt-0.5" data-id="element-3268" data-mp="16kvqul8jauql">
                                    + delivery
                                  </p>
                                </>}
                            </div>}
                        </div>

                        {/* Section-wide explainer pulldown for the
                         Small-load rate label. Spans the entire price
                         card so it has room to breathe (rather than
                         squeezing into the left column under the
                         numbers). */}
                        <AnimatePresence data-id="element-3269" data-mp="3ntoy5iccxvlq">
                          {showSmallLoadInfo && !isCustomQuoteZone && showsEffective && hasSurchargedLoad && !bulkPct && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-3270" data-mp="39t5vyrukpjrh">
                                <p className="mt-3 p-3 rounded-lg bg-[#CB6E0A]/10 dark:bg-[#F6A93B]/10 border border-[#CB6E0A]/30 dark:border-[#F6A93B]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed" data-id="element-3271" data-mp="2v4hyuh52x1zf">
                                  A small load runs the truck below capacity.
                                  Fixed loading costs (loader operator, scale,
                                  ticketing) get split across fewer yards, so
                                  the per-yard rate goes up. Order{' '}
                                  {smallLoadThreshold}+ yd in a single delivery
                                  to drop to the base ${product.pricePerYard}/yd
                                  rate.
                                </p>
                              </motion.div>}
                        </AnimatePresence>

                        {/* Materials breakdown — ALWAYS visible when
                         there's a quantity. Surfaces the per-yard rate
                         in plain language: single small load → 1
                         "Small-load rate" line; single full load or
                         all-full multi-load → 1 "Full-load rate" (or
                         bulk-discount) line; full + partial mix → 2
                         lines so the customer sees exactly which yards
                         run at each rate. */}
                        {billableYards > 0 && !isCustomQuoteZone && (() => {
                    const _fullLoadCount = Math.floor(billableYards / fullLoad);
                    const _fullLoadYards = _fullLoadCount * fullLoad;
                    const _partialYards = billableYards - _fullLoadYards;
                    const _partialSurcharge = _partialYards > 0 ? getSmallLoadSurcharge(_partialYards, product.name) : 1;
                    // Derive the Materials-breakdown numbers
                    // from the source-of-truth `totalCost`
                    // (which has the bulk discount applied at
                    // the whole-order level with proper
                    // rounding). The OLD approach rounded the
                    // per-yard rate first then multiplied,
                    // which drifted from totalCost by several
                    // dollars on bulk-discounted orders
                    // (e.g. 24 yd @ 5% showed $73/yd × 24 =
                    // $1,752 while the actual total was
                    // $1,733). Now the breakdown ALWAYS
                    // reconciles to totalCost exactly.
                    //
                    // Strategy:
                    //   1. Compute the full-load subtotal as
                    //      the pro-rated share of totalCost
                    //      that the full-load yards represent
                    //      (using the un-surcharged base
                    //      rate × discounted price). For pure
                    //      full-load orders this equals
                    //      totalCost.
                    //   2. Compute partial subtotal as
                    //      totalCost − fullLoadSubtotal so
                    //      the two lines always sum to the
                    //      grand total.
                    //   3. Per-yard rates are derived from
                    //      the subtotals so they match what
                    //      the customer is actually paying.
                    const _partialBaseShare = _partialYards * _partialSurcharge;
                    const _fullLoadBaseShare = _fullLoadYards;
                    const _baseShareSum = _fullLoadBaseShare + _partialBaseShare;
                    const fullLoadSubtotal = _fullLoadYards > 0 && _baseShareSum > 0 ? Math.round(totalCost * _fullLoadBaseShare / _baseShareSum) : 0;
                    const partialSubtotal = _partialYards > 0 ? totalCost - fullLoadSubtotal : 0;
                    const fullLoadPerYd = _fullLoadYards > 0 ? Math.round(fullLoadSubtotal / _fullLoadYards) : Math.round(product.pricePerYard * (1 - bulkPct));
                    const partialPerYd = _partialYards > 0 ? Math.round(partialSubtotal / _partialYards) : 0;
                    const hasMixedBreakdown = _fullLoadCount > 0 && _partialYards > 0;
                    const fullLoadTagFull = bulkPct > 0 ? <span className="text-[#04A05C] font-semibold" data-id="element-3272" data-mp="cf10n6hapwc4">
                                  {Math.round(bulkPct * 100)}% bulk discount
                                </span> : null;
                    return <div className="mt-4 pt-3 border-t border-brand-charcoal/10 dark:border-white/10 space-y-1.5" data-id="element-3273" data-mp="3w08ba65nfqfl">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-charcoal/60 dark:text-white/60 mb-1" data-id="element-3274" data-mp="dtan7cnz1wdq">
                                  Materials
                                </div>
                                {hasMixedBreakdown ? <>
                                    {/* Full-load line */}
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-3275" data-mp="3r2gjdc5084be">
                                      <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-3276" data-mp="3maoncm32x6dv">
                                        <span data-id="element-3277" data-mp="2yjt3qc8s1hhp">
                                          ${fullLoadPerYd}/yd × {_fullLoadYards}{' '}
                                          yd
                                        </span>
                                        {fullLoadTagFull}
                                      </div>
                                      <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3278" data-mp="2i1dw56deza3m">
                                        ${fullLoadSubtotal.toLocaleString()}
                                      </div>
                                    </div>
                                    {/* Partial-load line. Only tag it
                                     "Small-load rate" when the partial
                                     actually triggers a surcharge
                                     (below the threshold). A partial
                                     at/above the threshold — e.g. an
                                     11 yd remainder on a 35 yd heavy
                                     order — runs at the full-load rate,
                                     so it gets the full-load (or bulk)
                                     tag instead. */}
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-3279" data-mp="z2zjo80v7auf">
                                      <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-3280" data-mp="u3d0zj29p2e8">
                                        <span data-id="element-3281" data-mp="p638fujl0am6">
                                          ${partialPerYd}/yd × {_partialYards}{' '}
                                          yd
                                        </span>
                                        {_partialSurcharge > 1 ? <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} aria-expanded={showSmallLoadInfo} className="inline-flex items-center gap-1 text-[#CB6E0A] dark:text-[#F6A93B] font-semibold hover:opacity-90 transition-opacity" data-id="element-3282" data-mp="1d27k148rkp07">
                                            <span className="underline underline-offset-2 decoration-dotted" data-id="element-3283" data-mp="25kmmeb4yi4i6">
                                              Small-load rate
                                            </span>
                                            <Info className="w-3 h-3" data-id="element-3284" data-mp="2xi40hpu22u7j" />
                                          </button> : fullLoadTagFull}
                                      </div>
                                      <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3285" data-mp="3bfaj9kes4swr">
                                        ${partialSubtotal.toLocaleString()}
                                      </div>
                                    </div>
                                  </> : _partialYards > 0 && _partialSurcharge > 1 ? (
                      /* Single small-load order BELOW the
                      threshold — surcharge in effect,
                      tag the line "Small-load rate". */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-3286" data-mp="80534hukhaml">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-3287" data-mp="32lb8f4y4zc6">
                                      <span data-id="element-3288" data-mp="mliu66kfhrvj">
                                        ${partialPerYd}/yd × {_partialYards} yd
                                      </span>
                                      <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} aria-expanded={showSmallLoadInfo} className="inline-flex items-center gap-1 text-[#CB6E0A] dark:text-[#F6A93B] font-semibold hover:opacity-90 transition-opacity" data-id="element-3289" data-mp="dm5kpdwb13at">
                                        <span className="underline underline-offset-2 decoration-dotted" data-id="element-3290" data-mp="8ocfoo6qmfc4">
                                          Small-load rate
                                        </span>
                                        <Info className="w-3 h-3" data-id="element-3291" data-mp="3v2nhku3i4r7x" />
                                      </button>
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3292" data-mp="3h605tl6w3why">
                                      ${partialSubtotal.toLocaleString()}
                                    </div>
                                  </div>) : _partialYards > 0 ? (
                      /* At-or-above-threshold but below
                      one full load (e.g. 10–11 yd on
                      heavy materials) — surcharge = 1,
                      so it's effectively the full-load
                      rate. Render with the full-load
                      tag (or bulk-discount tag). */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-3293" data-mp="25lrvuvfa85em">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-3294" data-mp="21kg52mko9e4u">
                                      <span data-id="element-3295" data-mp="1wl3je55jed3u">
                                        ${partialPerYd}/yd × {_partialYards} yd
                                      </span>
                                      {fullLoadTagFull}
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3296" data-mp="2wul1kzmovjut">
                                      ${partialSubtotal.toLocaleString()}
                                    </div>
                                  </div>) : (
                      /* Pure full-load order (1+ full
                      loads, no partial) — one line at
                      the full-load rate or bulk-discount
                      rate. */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-3297" data-mp="3kj493cxwzslu">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-3298" data-mp="3piqkrhy3h0sj">
                                      <span data-id="element-3299" data-mp="3tjiusdesj9pv">
                                        ${fullLoadPerYd}/yd × {_fullLoadYards}{' '}
                                        yd
                                      </span>
                                      {fullLoadTagFull}
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3300" data-mp="13u1opnsncssv">
                                      ${fullLoadSubtotal.toLocaleString()}
                                    </div>
                                  </div>)}
                                {deliveryLoads.length > 1 && <p className="text-xs text-brand-orange mt-1" data-id="element-3301" data-mp="18t4sz60syuzs">
                                    + additional delivery fee for{' '}
                                    {deliveryLoads.length - 1} extra{' '}
                                    {deliveryLoads.length > 2 ? 'loads' : 'load'}
                                  </p>}
                                {deliveryLoads.some(l => l.yards > 0 && l.yards < 3) && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1" data-id="element-3302" data-mp="q2n3rbf6ihj6">
                                    Includes a $250 micro-load delivery fee
                                    (load under 3 yd).
                                  </p>}
                              </div>;
                  })()}
                      </div>;
              })()}

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#04A05C]/30 bg-[#04A05C]/10 p-3 sm:p-4" data-id="element-3303" data-mp="2vbtg4uqpzsf1">
                          <div className="flex items-start gap-3" data-id="element-3304" data-mp="2hc1vwc9m1fsl">
                            <div className="w-8 h-8 rounded-full bg-[#04A05C]/20 flex items-center justify-center shrink-0" data-id="element-3305" data-mp="2hdv43vzvg82p">
                              <Check className="w-4 h-4 text-[#04A05C]" data-id="element-3306" data-mp="fkoqzn7n77wj" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3307" data-mp="1xiacscvxdo8t">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-3308" data-mp="1xfxutzq63dvr">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-3309" data-mp="2b8x8dw6e2y0o">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-3310" data-mp="1sixnllhl0532">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-3311" data-mp="2fmbvh1jvai12">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-2" data-id="element-3312" data-mp="2ya76ido3hhqh">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-3313" data-mp="2tdgqtrg7co5l">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-3314" data-mp="37cgfgz3pt31r">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-3315" data-mp="yugi6vqc6zy7">
                            5% at {tier2Yards} yd · 10% at {tier3Yards} yd
                          </span>;
                  })()}
                    </div>
                    {/* Floating stepper — no container background, just
                     big bold + and − pads flanking the live readout.
                     active:* gives instant green flash on press. Below
                     a full load increments by 1 yard; at/above a full
                     load snaps to the next full-load multiple (2, 3,
                     4 loads). + at 4 loads flips the row below into an
                     amber "Need 5+ loads?" link (does NOT auto-open the
                     custom-quote modal — the customer explicitly taps
                     the link). */}
                    <div className="flex flex-col gap-3" data-id="element-3316" data-mp="25hlpmklcetrk">
                      <div className="flex items-stretch gap-3 min-h-[68px] sm:min-h-[79px]" data-id="element-3317" data-mp="25h3p3brndtdu">
                        <div className="w-1/2 flex items-center px-5 sm:px-6 rounded-lg border text-2xl sm:text-3xl font-extrabold tabular-nums leading-none border-stone-300 bg-transparent text-brand-charcoal dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white" data-id="element-3318" data-mp="390771obyl71t">
                          {(() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        const loadCount = current > fullLoad ? Math.round(current / fullLoad) : 1;
                        const isFullLoad = current === fullLoad;
                        const isMin = current === 3;
                        const commit = () => {
                          const parsed = parseInt(qtyDraft, 10);
                          if (isNaN(parsed) || parsed <= 0) {
                            // Empty / invalid → revert input text to current.
                            setQtyDraft(String(current));
                            return;
                          }
                          // Cap at 8 loads (96 yd heavy / 120 yd light).
                          const max = fullLoad * 8;
                          const clamped = Math.min(max, Math.max(1, parsed));
                          setQuantity(String(clamped));
                          setQtyDraft(String(clamped));
                          // Flip the amber "text us" CTA once past 4 loads.
                          if (clamped > fullLoad * 4) {
                            setShowQuoteWarning(true);
                          } else {
                            setShowQuoteWarning(false);
                          }
                        };
                        return <>
                                <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                            // Numeric only, max 2 digits.
                            const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                            setQtyDraft(v);
                          }} onBlur={commit} onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.currentTarget as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setQtyDraft(String(current));
                              (e.currentTarget as HTMLInputElement).blur();
                            }
                          }} aria-label="Quantity in yards" style={{
                            width: `${Math.max(1, qtyDraft.length)}ch`
                          }} className={`bg-transparent border-0 outline-none text-center text-2xl font-bold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3319" data-mp="1q1sqoewagnnc" />
                                <span className={`hidden sm:inline transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3320" data-mp="cyan8y9eiimt">
                                  {' '}
                                  yards
                                </span>
                                <span className={`sm:hidden transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3321" data-mp="3v848hdv3qxc2">
                                  {' '}
                                  yd
                                </span>
                                {(loadCount > 1 || loadCount === 1 && Number(quantity) === (isLight ? 15 : 12)) && <span className="ml-2 text-xs sm:text-sm font-medium text-brand-muted dark:text-stone-400 align-baseline whitespace-nowrap" data-id="element-3322" data-mp="1jjd1ju7yk378">
                                    ({loadCount}{' '}
                                    {loadCount === 1 ? 'load' : 'loads'})
                                  </span>}
                              </>;
                      })()}
                        </div>
                        <div className="flex-1 flex items-center justify-end gap-4 sm:gap-5" data-id="element-3323" data-mp="1cnjx9gv60em2">
                          <button type="button" onClick={() => {
                        const fullLoad = isLight ? 15 : 12;
                        const MIN = 3;
                        const current = Number(quantity) || 12;
                        if (current <= fullLoad) {
                          if (current > MIN) setQuantity(String(current - 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          const newLoads = currentLoads - 1;
                          setQuantity(String(newLoads * fullLoad));
                          // Clear the amber CTA once back at 4 loads or less.
                          if (newLoads <= 4) setShowQuoteWarning(false);
                        }
                      }} aria-label="Decrease quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="group flex items-center justify-center rounded-full border w-16 h-16 sm:w-[72px] sm:h-[72px] shrink-0 transition-colors duration-150 border-stone-300 bg-transparent text-brand-charcoal dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white hover:bg-[#059F40]/10 dark:hover:bg-[#333333] active:bg-[#059F40]/20 active:text-[#059F40] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c]" data-id="element-3324" data-mp="1hlldzv54c1ud">
                            <Minus className="w-8 h-8 sm:w-9 sm:h-9 transition-transform duration-150 ease-out group-active:scale-125" strokeWidth={2.5} data-id="element-3325" data-mp="y37andrtdllv" />
                          </button>
                          <button type="button" onClick={() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        if (current < fullLoad) {
                          setQuantity(String(current + 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          // Cap at 8 loads. Past 4 loads the customer
                          // is in "large order" territory and the
                          // primary CTA flips to the amber "text us"
                          // button — but the stepper still works so
                          // they can show us the exact yardage they
                          // need before tapping through to SMS.
                          if (currentLoads >= 8) return;
                          const newLoads = currentLoads + 1;
                          setQuantity(String(newLoads * fullLoad));
                          if (newLoads >= 5) setShowQuoteWarning(true);
                        }
                      }} aria-label="Increase quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="group flex items-center justify-center rounded-full border w-16 h-16 sm:w-[72px] sm:h-[72px] shrink-0 transition-colors duration-150 border-stone-300 bg-transparent text-brand-charcoal dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white hover:bg-[#059F40]/10 dark:hover:bg-[#333333] active:bg-[#059F40]/20 active:text-[#059F40] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c]" data-id="element-3326" data-mp="17xg3k2krbgkk">
                            <Plus className="w-8 h-8 sm:w-9 sm:h-9 transition-transform duration-150 ease-out group-active:scale-125" strokeWidth={2.5} data-id="element-3327" data-mp="35lyxcdcm9g59" />
                          </button>
                        </div>
                      </div>

                      {/* Large-order handoff — when + has pushed past
                       4 loads, the primary CTA below morphs into an
                       amber "Text us for a quote" button instead of a
                       separate amber pulldown. */}

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="space-y-3" data-id="element-3328" data-mp="3k8alfo0x8k4b">
                          <div className="w-full flex items-center justify-center bg-[#04A05C]/10 dark:bg-[#006241]/40 text-[#04A05C] dark:text-[#22c95c] rounded-none font-bold border border-[#04A05C] dark:border-[#22c95c] py-4 text-lg" data-id="element-3329" data-mp="3g88n9ouea8ts">
                            <Check className="w-5 h-5 mr-2" data-id="element-3330" data-mp="bsz8yrzhur5q" />
                            Added to Truck!
                          </div>
                          <div className="flex gap-2" data-id="element-3331" data-mp="3u5xbn6no7y3l">
                            <button onClick={() => {
                        setAddedFlash(false);
                        navigate('/#materials');
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-3332" data-mp="30owf414m9lb">
                              Continue Shopping
                            </button>
                            <button onClick={() => {
                        setAddedFlash(false);
                        openCart();
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-3333" data-mp="1en607mn80w1y">
                              <ShoppingCart className="w-4 h-4" data-id="element-3334" data-mp="hxow3c3vzi2d" />
                              View Cart
                            </button>
                          </div>
                        </motion.div> : showQuoteWarning ? <button onClick={() => {
                    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    if (!isMobile) {
                      setIsQuoteModalOpen(true);
                      return;
                    }
                    const fullLoad = isLight ? 15 : 12;
                    const yds = Number(quantity) || fullLoad * 5;
                    const loads = Math.max(5, Math.round(yds / fullLoad));
                    const body = `Hi 206Gravel — I need a custom quote for ${loads} loads of ${product.name} (${yds} yd). Thanks!`;
                    window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                  }} className="w-full py-[18px] rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center bg-[#DD4132]/15 dark:bg-[#DD4132]/15 hover:bg-[#DD4132]/25 dark:hover:bg-[#DD4132]/25 text-[#DD4132] dark:text-[#DD4132] border-2 border-[#DD4132]/40 hover:border-[#DD4132]/60" aria-live="polite" data-id="element-3335" data-mp="qvh55yj0lthb">
                          <MessageSquare className="w-5 h-5 mr-2" data-id="element-3336" data-mp="xms0v53j0gzb" />
                          Large order — text us →
                        </button> : (() => {
                    const current = Number(quantity) || 12;
                    const fullLoad = isLight ? 15 : 12;
                    const loadPct = Math.min(100, Math.max(0, current / fullLoad * 100));
                    return <button onClick={handleAddToCart} className="relative w-full overflow-hidden bg-[#04702E] text-white py-[21px] rounded-none font-bold text-lg flex items-center justify-center shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-3337" data-mp="1a42tbj9gkezu">
                              <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 bg-[#0DA24B]" initial={false} animate={{
                        width: `${loadPct}%`
                      }} transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 26
                      }} data-id="element-3338" data-mp="1j4r507bdq39k" />
                              <span className="relative z-10 flex items-center justify-center" data-id="element-3339" data-mp="21wjptegaoi67">
                                <Truck className="w-5 h-5 mr-2" data-id="element-3340" data-mp="26u3el8amcdce" />
                                Add to Truck
                              </span>
                            </button>;
                  })()}
                    </div>

                    <div className="flex justify-center" data-id="element-3341" data-mp="2slageoqeaxd">
                      <SameDayBadge variant="full" data-id="element-3342" data-mp="giyh8dpvzjl4" />
                    </div>

                    {/* Material sanity check — helps the customer confirm
                     the product they're viewing matches their project
                     BEFORE adding to cart. Lives here (not in the cart
                     drawer) so the decision happens with intent fresh.
                     The helper is now category-aware: each category in
                     data/products.ts ships its own pair of questions
                     and its own recommendation table inside
                     components/CartHelper.tsx, so the suggestions stay
                     relevant whether the customer is browsing base rock,
                     clear & drain rock, soil/mulch, quarry rip-rap, or
                     decorative landscape stone. */}
                    <CartHelper contextProduct={{
                  slug: product.slug,
                  name: product.name
                }} data-id="element-3343" data-mp="1bs21wk32cysb" />

                    {/* Inline Coverage Calculator — pulldown style, matches
                     the materials hub (Services) calculator. Replaces the
                     old fixed side drawer so the experience is identical
                     whether the customer arrives via the hub or a direct
                     /shop/:slug link. Reuses the existing calculator
                     state (shape/length/width/diameter/base/height/depth
                     /calcYards/calcTons) and `handleUseQuantity()`. */}
                    <div className="mt-3 pt-2.5" data-id="element-3344" data-mp="o1633306x3bk">
                      <button onClick={() => setIsCalcOpen(v => !v)} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-orange hover:text-brand-orangeHover transition-colors" aria-expanded={isCalcOpen} data-id="element-3345" data-mp="o40oawr12s61">
                        <CalcIcon className="w-4 h-4" data-id="element-3346" data-mp="1tpujism8bfoc" />
                        {isCalcOpen ? 'Hide Calculator' : 'Calculate how much I need'}
                      </button>
                      <AnimatePresence data-id="element-3347" data-mp="1jr53l5dw85aq">
                        {isCalcOpen && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-3348" data-mp="1jpk89djr8ibt">
                            <div className="mt-4" data-id="element-3349" data-mp="w1kkmmhqp81g">
                              <SimpleAreaCalculator materialSlug={product.slug} onApply={handleUseQuantity} data-id="element-3350" data-mp="w2vjq908prm8" />
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    {/* Multi-load delivery-fee notice — visible whenever
                     the stepper is open. The "please select how many"
                     prompt is no longer needed because the stepper
                     auto-seeds 2 loads on entering multi mode. */}
                    <AnimatePresence data-id="element-3351" data-mp="37mtdo6nzjmiu">
                      {quantity === 'multi' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-3352" data-mp="3cmxu5wow8h92">
                          <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-3353" data-mp="2tp4tz25nmlfd">
                            <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-3354" data-mp="1waisalbkmsof" />
                            <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-3355" data-mp="218aovsn5v2yy">
                              Multi-load orders require separate deliveries. You
                              will be charged a delivery fee per load.
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Standalone "Get a Custom Quote" button removed —
                   the amber "Need 5+ loads? Get a custom quote →" link
                   inside the readout panel above is now the single,
                   unambiguous entry point to the quote modal. */}

                  {/* "Need a custom amount? Call us..." line removed —
                   customers can dial in any 3-yd-stepped amount via
                   the quantity dropdown and run the inline calculator
                   above for project-based sizing.
                   "Quiz & Calculator" pill also removed — the inline
                   calculator above and the in-context CartHelper cover
                   both of those jobs without bouncing the user away. */}
                  <div className="flex flex-wrap items-center justify-center gap-2 pt-3 mt-1 border-t border-brand-charcoal/15 dark:border-white/15" data-id="element-3356" data-mp="ci3ti62et5h9">
                    <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-3357" data-mp="3g48z3s6rj9k">
                      Compare Materials
                    </Link>
                    <Link to="/blog" className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-3358" data-mp="rd1j4r66bgun">
                      DIY Guide
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-3359" data-mp="366e889e7rvdh" />

      {/* Old fixed-position Calculator Drawer removed — replaced by the
                                                                                                                                                                                                                                                                          inline "Calculate how much I need" pulldown under the Add to Truck
                                                                                                                                                                                                                                                                          button, matching the Services hub product detail layout. */}
    </motion.main>;
}